import { motion } from "framer-motion";
import { Check } from "@phosphor-icons/react";

const EASE = [0.16, 1, 0.3, 1];

const ROWS = [
  {
    network: "All (except Ethereum)",
    single: { gas: "$0.02", fee: "0.1%" },
    recurring: { gas: "$0.02", fee: "0.2%" },
  },
  {
    network: "Ethereum",
    single: { gas: "$0.25", fee: "0.1%" },
    recurring: { gas: "$0.25", fee: "0.2%" },
  },
];

const INCLUDED = [
  "Wallet-to-wallet transfers",
  "Real-time processing",
  "Multi-chain support",
  "Self-custody by default",
  "No monthly fees",
];

const Cell = ({ data }) => (
  <div>
    <p className="font-display text-xl md:text-2xl font-bold">
      {data.gas} <span className="text-zinc-500 font-body font-normal text-base">gas</span>
    </p>
    <p className="mt-1 text-zinc-700">
      + <span className="text-[#0a0a0a] font-semibold underline decoration-[#f5d949] decoration-2 underline-offset-4">{data.fee}</span> transaction fee
    </p>
    <p className="mt-2 text-xs font-mono text-zinc-500">Max fee: $5 / transaction</p>
  </div>
);

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative bg-[#f6f6f7] py-24 md:py-32 border-t border-black/5">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f5d949]" />
            Pricing
          </p>
          <h2 className="mt-5 font-display font-black tracking-[-0.02em] leading-[0.95] text-4xl md:text-6xl">
            Simple, transparent pricing.
          </h2>
        </div>

        {/* Desktop table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-14 hidden md:block rounded-3xl border border-black/10 bg-white overflow-hidden"
        >
          <div className="grid grid-cols-3 bg-zinc-50 border-b border-black/10">
            <div className="p-6 text-sm uppercase tracking-[0.15em] text-zinc-500 font-semibold">Network</div>
            <div className="p-6 text-sm uppercase tracking-[0.15em] text-zinc-500 font-semibold">Single Deposit</div>
            <div className="p-6 text-sm uppercase tracking-[0.15em] text-zinc-500 font-semibold">Recurring Deposit</div>
          </div>
          {ROWS.map((r, i) => (
            <div
              key={r.network}
              data-testid={`pricing-row-${i}`}
              className={`grid grid-cols-3 items-center ${i !== ROWS.length - 1 ? "border-b border-black/10" : ""} hover:bg-black/[0.015] transition-colors`}
            >
              <div className="p-6 font-display text-xl font-bold pr-4">{r.network}</div>
              <div className="p-6"><Cell data={r.single} /></div>
              <div className="p-6"><Cell data={r.recurring} /></div>
            </div>
          ))}
        </motion.div>

        {/* Mobile cards */}
        <div className="mt-12 md:hidden flex flex-col gap-5">
          {ROWS.map((r, i) => (
            <div key={r.network} data-testid={`pricing-card-${i}`} className="rounded-2xl border border-black/10 bg-white p-6">
              <h3 className="font-display text-xl font-bold">{r.network}</h3>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Single</p>
                  <Cell data={r.single} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Recurring</p>
                  <Cell data={r.recurring} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Included */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-10 rounded-3xl bg-zinc-100 border border-black/10 text-[#0a0a0a] p-8 md:p-10"
        >
          <p className="font-display text-2xl font-black mb-6">Included with every transaction</p>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {INCLUDED.map((item) => (
              <div key={item} className="flex items-center gap-2.5" data-testid={`included-${item.replace(/\s+/g, "-").toLowerCase()}`}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#f5d949]">
                  <Check size={15} weight="bold" className="text-black" />
                </span>
                <span className="font-semibold">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
